import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import senderIdRequests from "../../api/SenderIdRequests";
import Loader from "../../components/utils/Loader";
import Navbar from "../../components/dashboard/Navbar";
import {
  Search,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Download,
  MessageSquare,
  Users,
  Check,
  X,
  Filter,
  Calendar,
  ArrowLeft,
  ArrowRight,
  MoreVertical,
  Eye,
  FileText,
} from "lucide-react";

const SenderId = () => {
  const [loading, setLoading] = useState(false);
  const [senderIds, setSenderIds] = useState([]);
  const [senderIdStats, setSenderIdStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [actionType, setActionType] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1,
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Fetch data with pagination and filters
const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    // Prepare query parameters
    const params = {
      page: currentPage,
      limit: itemsPerPage,
      ...(searchTerm && { search: searchTerm }),
      ...(statusFilter !== "all" && { status: statusFilter }),
      ...(sortField && { sort_by: sortField }),
      ...(sortDirection && { sort_order: sortDirection }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    };

    //console.log("Fetching with params:", params);

    // Fetch sender IDs with pagination
    const [senderResponse, statsResponse] = await Promise.all([
      senderIdRequests.getSenderIds(
        params.page,
        params.limit,
        params.search,
        params.sort_by,
        params.sort_order,
        params.status,
        params.date_from,
        params.date_to
      ),
      senderIdRequests.senderIdStats(),
    ]);

    //console.log("Sender Response:", senderResponse);
    //console.log("Stats Response:", statsResponse);

    // Handle sender IDs response
    if (senderResponse.success) {
      // Data should already be an array from SenderIdRequests
      const senderData = senderResponse.data || [];
      //console.log("Sender Data (array):", senderData);
      
      setSenderIds(senderData);
      
      // Set pagination from API response
      if (senderResponse.pagination) {
        setPagination({
          current_page: senderResponse.pagination.current_page || currentPage,
          total: senderResponse.pagination.total || senderData.length,
          per_page: senderResponse.pagination.per_page || itemsPerPage,
          last_page: senderResponse.pagination.last_page || 1,
          from: senderResponse.pagination.from || 1,
          to: senderResponse.pagination.to || Math.min(itemsPerPage, senderData.length),
        });
      } else {
        setPagination({
          current_page: currentPage,
          total: senderData.length,
          per_page: itemsPerPage,
          last_page: Math.ceil(senderData.length / itemsPerPage),
          from: 1,
          to: Math.min(itemsPerPage, senderData.length),
        });
      }
    } else {
      console.error("Failed to fetch sender IDs:", senderResponse.message);
      setSenderIds([]);
      setPagination({
        current_page: 1,
        total: 0,
        per_page: itemsPerPage,
        last_page: 1,
        from: 1,
        to: 0,
      });
      toast.error(senderResponse.message || "Failed to fetch sender IDs");
    }

    // Handle stats response
    if (statsResponse.success && statsResponse.data) {
      const statsData = statsResponse.data;
      //console.log("Stats Data:", statsData);
      setSenderIdStats(statsData);
    } else {
      console.warn("Failed to fetch stats:", statsResponse.message);
      setSenderIdStats({});
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    toast.error("An error occurred while fetching data");
    setSenderIds([]);
    setSenderIdStats({});
    setPagination({
      current_page: 1,
      total: 0,
      per_page: itemsPerPage,
      last_page: 1,
      from: 1,
      to: 0,
    });
  } finally {
    setLoading(false);
  }
}, [currentPage, itemsPerPage, searchTerm, statusFilter, sortField, sortDirection, dateFrom, dateTo]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, dateFrom, dateTo]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Handle export
const handleExport = async () => {
  try {
    // Show loading toast
    const toastId = toast.loading("Preparing export...");
    
    // Prepare export parameters (use current filters)
    const exportParams = {
      search: searchTerm,
      status: statusFilter !== "all" ? statusFilter : "",
      date_from: dateFrom,
      date_to: dateTo,
    };

    // Try API export first
    const result = await senderIdRequests.exportSenderIds(
      exportParams.search,
      exportParams.status,
      exportParams.date_from,
      exportParams.date_to
    );

    if (result.success) {
      toast.update(toastId, {
        render: "Export completed successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } else {
      // If API export failed, check if it's a 404 and use client-side
      if (result.message?.includes("404") || result.message?.includes("not found")) {
        toast.update(toastId, {
          render: "Using client-side export...",
          type: "info",
          isLoading: true,
          autoClose: false,
        });
        
        // Try client-side export
        const clientSideResult = await senderIdRequests.exportClientSide(
          exportParams.search,
          exportParams.status,
          exportParams.date_from,
          exportParams.date_to
        );
        
        if (clientSideResult.success) {
          toast.update(toastId, {
            render: "Export completed successfully (client-side)!",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
        } else {
          toast.update(toastId, {
            render: clientSideResult.message || "Export failed",
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
        }
      } else {
        toast.update(toastId, {
          render: result.message || "Export failed",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
    }
  } catch (error) {
    console.error("Export error:", error);
    toast.error("Failed to export data");
  }
};

  // Handle approve/reject actions
  const handleActionClick = (sender, type) => {
    setSelectedSender(sender);
    setActionType(type);
    setRejectReason("");
    setShowActionModal(true);
  };

  const handleApproveReject = async () => {
    if (actionType === "reject" && !rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        status: actionType === "approve" ? "Approved" : "Rejected",
        sender_slug: selectedSender.sender_slug,
      };

      if (actionType === "reject") {
        payload.reject_reason = rejectReason.trim();
        payload.rejected_date = new Date().toISOString().slice(0, 19).replace("T", " ");
      }

      const response = await senderIdRequests.updateStatus(payload);

      if (response.success) {
        const currentStatus = selectedSender.status;
        const newStatus = actionType === "approve" ? "Approved" : "Rejected";

        if (currentStatus === "Pending") {
          toast.success(
            `Sender ID ${
              actionType === "approve" ? "approved" : "rejected"
            } successfully`
          );
        } else {
          toast.success(
            `Sender ID status changed from ${currentStatus} to ${newStatus} successfully`
          );
        }

        // Refresh data
        fetchData();

        setShowActionModal(false);
        setSelectedSender(null);
        setActionType("");
        setRejectReason("");
      } else {
        toast.error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedSender(null);
    setActionType("");
    setRejectReason("");
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: {
        icon: CheckCircle,
        color: "bg-green-100 text-green-800 border-green-200",
        text: "Approved",
      },
      pending: {
        icon: Clock,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        text: "Pending",
      },
      rejected: {
        icon: XCircle,
        color: "bg-red-100 text-red-800 border-red-200",
        text: "Rejected",
      },
    };

    const statusKey = status?.toLowerCase() || "pending";
    const config = statusConfig[statusKey] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {status || "Pending"}
      </span>
    );
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    // Always show first page
    buttons.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={`px-3 py-1 text-sm font-medium rounded-md ${
          current === 1
            ? "text-white bg-blue-600"
            : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
        }`}
      >
        1
      </button>
    );

    // Calculate range of pages to show
    let startPage = Math.max(2, current - 1);
    let endPage = Math.min(totalPages - 1, current + 1);

    // Add ellipsis if needed
    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis-start" className="px-2 text-gray-500">
          ...
        </span>
      );
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      if (i > 1 && i < totalPages) {
        buttons.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              current === i
                ? "text-white bg-blue-600"
                : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
            }`}
          >
            {i}
          </button>
        );
      }
    }

    // Add ellipsis if needed
    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="ellipsis-end" className="px-2 text-gray-500">
          ...
        </span>
      );
    }

    // Always show last page if there is more than 1 page
    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`px-3 py-1 text-sm font-medium rounded-md ${
            current === totalPages
              ? "text-white bg-blue-600"
              : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  // Clear date filters
  const clearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setShowDateFilter(false);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading && senderIds.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Sender ID Management
                </h1>
                <p className="mt-2 text-gray-600">
                  Manage and monitor sender ID requests
                </p>
              </div>
     
{/* <div className="flex gap-3">
  <button
    onClick={handleExport}
    disabled={loading || senderIds.length === 0}
    className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <Download className="w-4 h-4" />
    Export
  </button>
</div> */}
            </div>
          </div>

          {/* Stats Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-4"
          >
            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Sender IDs
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {senderIdStats.count_senderid || 0}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">
                    {senderIdStats.count_pending_senderid || 0}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-xl">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">Approved</p>
                  <p className="text-3xl font-bold text-green-600">
                    {senderIdStats.count_approved_senderid || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">Rejected</p>
                  <p className="text-3xl font-bold text-red-600">
                    {senderIdStats.count_rejected_senderid || 0}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Filters and Search */}
          <div className="p-6 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-1">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search sender IDs, user slugs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>

                {/* Date Filter Button */}
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className="flex gap-2 items-center px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  <Filter className="w-4 h-4" />
                  Date Filter
                  {(dateFrom || dateTo) && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  )}
                </button>
              </div>

              {/* Results Count */}
              <div className="text-sm text-gray-600">
                Showing {senderIds.length} of {pagination.total} results
              </div>
            </div>

            {/* Date Filter Dropdown */}
            {showDateFilter && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 gap-4 pt-4 mt-4 border-t border-gray-200 sm:grid-cols-2"
              >
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    From Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    To Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                {(dateFrom || dateTo) && (
                  <div className="sm:col-span-2">
                    <button
                      onClick={clearDateFilters}
                      className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                    >
                      Clear Date Filters
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Data Table */}
         {/* Data Table */}
<div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
            <button
              onClick={() => handleSort("sender")}
              className="flex gap-1 items-center hover:text-gray-700"
            >
              Sender ID
              {sortField === "sender" &&
                (sortDirection === "asc" ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                ))}
            </button>
          </th>
          <th className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
            User
          </th>
          <th className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
            <button
              onClick={() => handleSort("status")}
              className="flex gap-1 items-center hover:text-gray-700"
            >
              Status
              {sortField === "status" &&
                (sortDirection === "asc" ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                ))}
            </button>
          </th>
          <th className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
            <button
              onClick={() => handleSort("created_at")}
              className="flex gap-1 items-center hover:text-gray-700"
            >
              Created
              {sortField === "created_at" &&
                (sortDirection === "asc" ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                ))}
            </button>
          </th>
          <th className="px-6 py-4 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        <AnimatePresence>
          {Array.isArray(senderIds) && senderIds.length > 0 ? (
            senderIds.map((item, index) => (
              <motion.tr
                key={item.id || index}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {item.sender || "N/A"}
                  </div>
                </td>
                {/* <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8">
                      <div className="flex justify-center items-center w-8 h-8 bg-gray-300 rounded-full">
                        <Users className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div
                        className="text-sm font-medium text-gray-900"
                        title={item.user_slug}
                      >
                        {item.user_details.email?.substring(0, 15) || "N/A"}...
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {item.user_details.phone_number || "N/A"}
                      </div>
                    </div>
                  </div>
                </td> */}
                <td className="px-6 py-4 whitespace-nowrap">
  <div className="flex items-center">
    <div className="flex-shrink-0 w-8 h-8">
      <div className="flex justify-center items-center w-8 h-8 bg-gray-300 rounded-full">
        <Users className="w-4 h-4 text-gray-600" />
      </div>
    </div>
    <div className="ml-4">
      <div
        className="text-sm font-medium text-gray-900"
        title={item.user_slug}
      >
        {item.user_details?.email?.substring(0, 15) || "N/A"}...
      </div>
      <div className="text-sm text-gray-500">
        ID: {item.user_details?.phone_number || item.user_slug || "N/A"}
      </div>
    </div>
  </div>
</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(item.status)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                  {item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                  <div className="flex gap-2 justify-end items-center">
                    {item.status === "Pending" && (
                      <>
                        <button
                          onClick={() => handleActionClick(item, "approve")}
                          className="p-2 text-green-600 rounded-lg transition-colors hover:text-green-700 hover:bg-green-50"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleActionClick(item, "reject")}
                          className="p-2 text-red-600 rounded-lg transition-colors hover:text-red-700 hover:bg-red-50"
                          title="Reject"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {item.status === "Approved" && (
                      <button
                        onClick={() => handleActionClick(item, "reject")}
                        className="p-2 text-red-600 rounded-lg transition-colors hover:text-red-700 hover:bg-red-50"
                        title="Change to Rejected"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {item.status === "Rejected" && (
                      <button
                        onClick={() => handleActionClick(item, "approve")}
                        className="p-2 text-green-600 rounded-lg transition-colors hover:text-green-700 hover:bg-green-50"
                        title="Change to Approved"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {/* <button
                      onClick={() => toast.info("View details coming soon")}
                      className="p-2 text-blue-600 rounded-lg transition-colors hover:text-blue-700 hover:bg-blue-50"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button> */}
                  </div>
                </td>
              </motion.tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="px-6 py-8 text-center">
                <AlertCircle className="mx-auto w-12 h-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  No sender IDs found
                </h3>
                <p className="mt-2 text-gray-500">
                  {searchTerm || statusFilter !== "all" || dateFrom || dateTo
                    ? "Try adjusting your search or filter criteria."
                    : "No sender ID requests have been submitted yet."}
                </p>
              </td>
            </tr>
          )}
        </AnimatePresence>
      </tbody>
    </table>
  </div>

  {/* Pagination */}
  {Array.isArray(senderIds) && senderIds.length > 0 && pagination.last_page > 1 && (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div className="text-sm text-gray-700">
          Showing {pagination.from || 1} to {pagination.to || Math.min(itemsPerPage, senderIds.length)} of{" "}
          {pagination.total || senderIds.length} results
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
            className="flex gap-1 items-center px-3 py-1 text-sm font-medium text-gray-500 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex gap-1">
            {renderPaginationButtons()}
          </div>

          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
            className="flex gap-1 items-center px-3 py-1 text-sm font-medium text-gray-500 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )}
</div>

          {/* Empty State */}
          {senderIds.length === 0 && !loading && (
            <div className="py-12 text-center">
              <AlertCircle className="mx-auto w-12 h-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No sender IDs found
              </h3>
              <p className="mt-2 text-gray-500">
                {searchTerm || statusFilter !== "all" || dateFrom || dateTo
                  ? "Try adjusting your search or filter criteria."
                  : "No sender ID requests have been submitted yet."}
              </p>
            </div>
          )}

          {/* Action Modal */}
          <AnimatePresence>
            {showActionModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50"
                onClick={closeActionModal}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="w-full max-w-md bg-white rounded-2xl shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Modal Header */}
                  <div
                    className={`p-6 border-b border-gray-200 rounded-t-2xl ${
                      actionType === "approve" ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    <div className="flex gap-3 items-center">
                      <div
                        className={`p-2 rounded-full ${
                          actionType === "approve" ? "bg-green-100" : "bg-red-100"
                        }`}
                      >
                        {actionType === "approve" ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {actionType === "approve" ? "Approve" : "Reject"} Sender ID
                        </h3>
                        <p className="text-sm text-gray-600">
                          {selectedSender?.sender}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6">
                    {actionType === "approve" ? (
                      <div className="space-y-4">
                        <p className="text-gray-700">
                          {selectedSender?.status === "Pending"
                            ? "Are you sure you want to approve this sender ID?"
                            : "Are you sure you want to change this sender ID status to Approved?"}
                        </p>
                        <div className="p-4 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-800">
                            <strong>Sender ID:</strong> {selectedSender?.sender}
                          </p>
                          <p className="text-sm text-green-800">
                            <strong>User ID:</strong> {selectedSender?.user_slug}
                          </p>
                          <p className="text-sm text-green-800">
                            <strong>Current Status:</strong> {selectedSender?.status}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-gray-700">
                          {selectedSender?.status === "Pending"
                            ? "Please provide a reason for rejecting this sender ID. This information will be shared with the user."
                            : "Please provide a reason for changing this sender ID status to Rejected. This information will be shared with the user."}
                        </p>
                        <div className="p-4 bg-red-50 rounded-lg">
                          <p className="text-sm text-red-800">
                            <strong>Sender ID:</strong> {selectedSender?.sender}
                          </p>
                          <p className="text-sm text-red-800">
                            <strong>User ID:</strong> {selectedSender?.user_slug}
                          </p>
                          <p className="text-sm text-red-800">
                            <strong>Current Status:</strong> {selectedSender?.status}
                          </p>
                        </div>
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            {selectedSender?.status === "Pending"
                              ? "Rejection"
                              : "Status Change"} Reason *
                          </label>
                          <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={`Enter the reason for ${
                              selectedSender?.status === "Pending"
                                ? "rejection"
                                : "changing status"
                            }...`}
                            className="px-3 py-2 w-full rounded-lg border border-gray-300 resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            rows={4}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="flex gap-3 p-6 border-t border-gray-200">
                    <button
                      onClick={closeActionModal}
                      disabled={isUpdating}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApproveReject}
                      disabled={
                        isUpdating ||
                        (actionType === "reject" && !rejectReason.trim())
                      }
                      className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                        actionType === "approve"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      {isUpdating ? (
                        <div className="flex gap-2 justify-center items-center">
                          <div className="w-4 h-4 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                          {actionType === "approve"
                            ? "Approving..."
                            : "Rejecting..."}
                        </div>
                      ) : actionType === "approve" ? (
                        "Approve"
                      ) : (
                        "Reject"
                      )}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default SenderId;